import React, { useState } from "react";
import { Col, CustomInput, Row } from "reactstrap";

import { ContentLoader } from "@common/components/util/ContentLoader";
import { Loading } from "@common/components/util/Loading";
import { Workbench } from "@common/components/Workbench";
import { ChildrenArgs } from "@common/components/Workbench/types";
import * as Constants from "@zoning/constants";
import { Help } from "./Help";
import { TaxonomyContent, ZoningWrapper } from "./ZoningWrapper";

const loadRoles = [
  "zoning-page-png",
  "zoning-pagehr-png",
  "zoning-predicted-json",
  "zoning-pagesmeta-json",
  "zoning-reviewed-json",
  "zoning-taxonomy-json",
];

type Props = Readonly<{
  review: boolean;
}>;

const isTaxonomyContent = (input: any): input is TaxonomyContent => {
  if (input === null || (typeof input !== "object" && Array.isArray(input))) {
    return false;
  }
  return input.role === "zoning-taxonomy-json";
};

export const ZoningWorkbench = ({ review }: Props) => {
  const [autoShrink, setAutoshrink] = useState(true);
  const settings = (
    <>
      <Row>
        <Col>
          <strong>Auto shrink</strong>
        </Col>
      </Row>
      <Row className="align-items-center">
        <Col xs={8}>Enable auto shrink</Col>
        <Col className="text-right" xs={4}>
          <CustomInput
            bsSize="lg"
            checked={autoShrink}
            id="toggleAutoShrink"
            onChange={() => {
              setAutoshrink(!autoShrink);
            }}
            type="switch"
          />
        </Col>
      </Row>
      <Row>
        <Col className="text-muted">
          Zoning.ml will attempt to automatically and intelligently shrink new zones.
        </Col>
      </Row>
    </>
  );
  return (
    <Workbench
      blockingStep={Constants.TASK_KIND}
      docs={<Help review={review} />}
      loadRoles={loadRoles}
      review={review}
      settings={settings}
      workbenchTitle={Constants.APP_TITLE}
    >
      {({
        actions: { checkinJob, saveContent },
        auth,
        contents,
        job,
        noteCount,
        notes,
        sidebarVisible,
      }: ChildrenArgs) => {
        const contentToLoad = contents.filter(
          c =>
            c.role === "zoning-predicted-json" ||
            c.role === "zoning-pagesmeta-json" ||
            c.role === "zoning-reviewed-json" ||
            c.role === "zoning-taxonomy-json",
        );
        return (
          <ContentLoader
            allowFailures={["zoning-reviewed-json", "zoning-taxonomy-json"]}
            auth={auth.authString}
            content={contentToLoad}
          >
            {results => {
              if (results.ready) {
                const initialZones = results.contents
                  .filter(c => c.role === "zoning-reviewed-json")
                  .map(c => {
                    if (c.contents) {
                      return typeof c.contents === "string" ? JSON.parse(c.contents) : c.contents;
                    }
                    const predicted = results.contents.find(
                      c2 => c2.role === "zoning-predicted-json" && c2.order === c.order,
                    );
                    if (predicted && predicted.contents) {
                      return typeof predicted.contents === "string"
                        ? JSON.parse(predicted.contents)
                        : predicted.contents;
                    }
                    throw new Error("Unable to find predicted or reviewed zones.");
                  })
                  .map(c => c.predictions || c.blocks);
                const pageConfidences = results.contents
                  .filter(c => c.role === "zoning-predicted-json")
                  .map(c => (typeof c.contents === "string" ? JSON.parse(c.contents) : c.contents))
                  .map(c => parseFloat(c.page_confidence) || 0);
                let pageDimensions : Array<any> = results.contents
                  .find(c => c.role === "zoning-pagesmeta-json")!
                  .contents.pages.map((p: any) => ({
                    height: p.images.height,
                    width: p.images.width,
                  }));
                
                if (job.parent_id) {
                  pageDimensions = pageDimensions.slice(job.metadata.first_page -1, job.metadata.last_page -1);
                }
                
                let taxonomy = results.contents.find(isTaxonomyContent);

                let resetToZones;
                if (!review) {
                  resetToZones = results.contents
                    .filter(c => c.role === "zoning-predicted-json")
                    .map(c => {
                      if (c.contents) {
                        return typeof c.contents === "string" ? JSON.parse(c.contents) : c.contents;
                      }
                      return [];
                    })
                    .map(c => c.predictions);
                }
                if (pageConfidences.length !== pageDimensions.length) {
                  throw new Error("Page count mismatch. Please report this to an administrator.");
                }
                /*const lowResImageCount = contents.filter(c => c.role === "zoning-page-png").length;
                const highResImageCount = contents.filter(c => c.role === "zoning-pagehr-png")
                  .length;
                if (lowResImageCount !== highResImageCount) {
                  throw new Error("Page count mismatch. Please report this to an administrator.");
                }
                if (lowResImageCount !== pageConfidences.length) {
                  throw new Error("Page count mismatch. Please report this to an administrator.");
                }*/
                return (
                  <ZoningWrapper
                    autoShrink={autoShrink}
                    checkinJob={checkinJob}
                    contents={contents}
                    initialZones={initialZones}
                    job={job}
                    noteCount={noteCount}
                    notes={notes}
                    pageConfidences={pageConfidences}
                    pageDimensions={pageDimensions}
                    resetToZones={resetToZones}
                    readonly={review || sidebarVisible}
                    saveContent={saveContent}
                    taxonomy={taxonomy}
                    token={auth.token}
                  />
                );
              }
              return <Loading progress={results.progress} text="Loading job details" />;
            }}
          </ContentLoader>
        );
      }}
    </Workbench>
  );
};
